import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { UseTimeZone } from '../../../common/hooks/useTimeZone'
import { Element } from '../../../common/html/element'
import { Table } from '../../../common/table'
import { FORMAT, formatDateTime } from '../../../helpers/datetime'
import { formatVal, isInValidValue } from '../../../helpers/value'
import { EMPTY_VALUE } from '../../../utils/common'
import { PopupPaymentInformation } from '../common/popupPaymentInformation'
import { PAYMENT_METHOD } from '../orderManagement/constants'
import { OrderStatusCell } from '../orderManagement/table/OrderStatusCell'
import { PaymentStatusCell } from '../orderManagement/table/PaymentStatusCell'
import { DetailCell } from './DetailCell'
import { PackageNameTypesCell } from './PackageNameTypesCell'
import { TableFooter } from './TableFooter'
import {
  PACKAGE_SUB_MANAGE_KEYS,
  PACKAGE_SUB_MANAGE_TITLE,
  TYPE_PACKAGE_SUBSCRIPTION,
} from './constants'
import { getDataItemByRowId } from './helpers'
import './i18n'
import {
  selectFilter,
  selectFilterByKey,
  selectLoadingSubscriptionAndPackageOrders,
  selectSubscriptionAndPackageOrdersData,
  selectSubscriptionAndPackageOrdersDataById,
  selectSubscriptionAndPackageOrdersIds,
} from './store/slice'
import {
  getDetailRequestContract,
  getSNPNewTransactionPreview,
  getSubscriptionAndPackageOrdersPaged,
} from './store/thunk'

export const TableComponent = () => {
  const dispatch = useDispatch()

  const ids = useSelector(selectSubscriptionAndPackageOrdersIds)
  const isLoading = useSelector(selectLoadingSubscriptionAndPackageOrders)
  const data = useSelector(selectSubscriptionAndPackageOrdersData)
  const locale = useSelector((state) => state.i18n.locale)

  const requestType = useSelector(selectFilterByKey('requestType'))
  const paymentStatus = useSelector(selectFilterByKey('paymentStatus'))
  const requestStatus = useSelector(selectFilterByKey('requestStatus'))
  const type = useSelector(selectFilterByKey('type'))
  const dateFrom = useSelector(selectFilterByKey('from'))
  const dateTo = useSelector(selectFilterByKey('to'))
  const page = useSelector(selectFilterByKey('page'))
  const pageSize = useSelector(selectFilterByKey('pageSize'))
  const allFilter = useSelector(selectFilter)

  const timeZone = UseTimeZone()

  const [isOpenPayNow, setIsOpenPayNow] = useState(false)
  const [orderId, setOrderId] = useState(null)

  const getDetailContract = (val) => {
    dispatch(getDetailRequestContract({ Id: val }))
  }

  const handlePayNow = (rowId) => {
    setOrderId(rowId)
    const dataRow = getDataItemByRowId(data, rowId)
    dispatch(
      getSNPNewTransactionPreview({
        transactionCode: dataRow?.transactionCode,
      }),
    )
    setIsOpenPayNow(true)
  }

  useEffect(() => {
    dispatch(
      getSubscriptionAndPackageOrdersPaged({
        OrderType: requestType?.value,
        TransactionStatus: paymentStatus?.value,
        OrderStatus: requestStatus?.value,
        TypeId: type?.value,
        CreateDateFrom: dateFrom,
        CreateDateTo: dateTo,
        Page: page,
        PageSize: pageSize,
      }),
    )
  }, [allFilter, locale])

  return (
    <>
      <Table
        ids={ids}
        isNoDataWithHead
        getDataFromRedux={selectSubscriptionAndPackageOrdersDataById}
        schema={getSchema(
          data,
          getDetailContract,
          handlePayNow,
          locale,
          timeZone,
        )}
        isLoading={isLoading}
        autoHeightMax={500}
        renderPagination={() => <TableFooter />}
      />
      {isOpenPayNow && (
        <PopupPaymentInformation
          isShow={isOpenPayNow}
          setIsShow={setIsOpenPayNow}
          orderId={orderId}
        />
      )}
    </>
  )
}

const getSchema = (data, getDetailContract, handlePayNow, locale, timeZone) => {
  return PACKAGE_SUB_MANAGE_TITLE.map((item) => {
    const result = {
      title: <Translate value={item.title} />,
      colId: item.key,
    }
    if (item.key === PACKAGE_SUB_MANAGE_KEYS.ORDER) {
      return {
        ...result,
        render: (val) => {
          return val
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.REQUEST_TYPE) {
      return {
        ...result,
        render: (val) => {
          return <Translate value={`subscriptionPayment.${val}`} />
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.TYPES) {
      return {
        ...result,
        render: (val, { rowId }) => {
          const type = getDataItemByRowId(data, rowId)['orderType'] || null
          if (type === TYPE_PACKAGE_SUBSCRIPTION.SUBSCRIPTION) {
            return val
          } else {
            return (
              <PackageNameTypesCell
                data={data}
                rowId={rowId}
                packageInfoKey={'details'}
                packageNameKey={'fiinGateReportTypeName'}
              />
            )
          }
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.DATE_REQUESTED) {
      return {
        ...result,
        render: (val) => {
          return formatDateTime(val, FORMAT.DATE, locale, timeZone)
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.TOTAL) {
      return {
        ...result,
        render: (val) => {
          return formatVal(val, locale, false, 0)
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.CONTRACT) {
      return {
        ...result,
        render: (val, { rowId }) => {
          return (
            <Element
              elementName="span"
              className="cursorPointer"
              style={{
                color: val === EMPTY_VALUE ? '#9C9C9C' : '#2C73E8',
                pointerEvents: val === EMPTY_VALUE ? 'none' : 'auto',
              }}
              onClick={() => getDetailContract(rowId)}
            >
              <Translate value="userManagement.packageSubManagement.DOWNLOAD" />
            </Element>
          )
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.DETAIL) {
      return {
        ...result,
        render: (val) => {
          return <DetailCell val={val} />
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.REQUEST_STATUS) {
      return {
        ...result,
        render: (val) => {
          return <OrderStatusCell val={val} />
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.PAYMENT_METHOD) {
      return {
        ...result,
        render: (val) => {
          return (
            !isInValidValue(val) &&
            val !== EMPTY_VALUE && <Translate value={PAYMENT_METHOD[val]} />
          )
        },
      }
    }

    if (item.key === PACKAGE_SUB_MANAGE_KEYS.PAYMENT_STATUS) {
      return {
        ...result,
        render: (val, { rowId }) => {
          const orderStatus = getDataItemByRowId(data, rowId)?.status
          return (
            <PaymentStatusCell
              val={val}
              orderStatus={orderStatus}
              handlePayNow={() => handlePayNow(rowId)}
            />
          )
        },
      }
    }

    return {
      ...result,
      render: (val) => {
        return val
      },
    }
  })
}
